import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateDiscountRuleTagsApplicationErrors,
  BulkUpdateDiscountRuleTagsByFilterApplicationErrors,
  BulkUpdateDiscountRuleTagsByFilterOptions,
  BulkUpdateDiscountRuleTagsByFilterResponse,
  BulkUpdateDiscountRuleTagsOptions,
  BulkUpdateDiscountRuleTagsResponse,
  CreateDiscountRuleApplicationErrors,
  CreateDiscountRuleValidationErrors,
  DiscountRule,
  DiscountRuleCreatedEnvelope,
  DiscountRuleDeletedEnvelope,
  DiscountRuleQuery,
  DiscountRuleUpdatedEnvelope,
  DiscountRulesQueryBuilder,
  QueryDiscountRulesResponse,
  UpdateDiscountRule,
  bulkUpdateDiscountRuleTags as universalBulkUpdateDiscountRuleTags,
  bulkUpdateDiscountRuleTagsByFilter as universalBulkUpdateDiscountRuleTagsByFilter,
  createDiscountRule as universalCreateDiscountRule,
  deleteDiscountRule as universalDeleteDiscountRule,
  getDiscountRule as universalGetDiscountRule,
  queryDiscountRules as universalQueryDiscountRules,
  typedQueryDiscountRules as universalTypedQueryDiscountRules,
  updateDiscountRule as universalUpdateDiscountRule,
} from './ecom-discounts-v1-discount-rule-discount-rules.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createDiscountRule(
  httpClient: HttpClient
): CreateDiscountRuleSignature {
  return (
    discountRule: NonNullablePaths<DiscountRule, `discounts` | `name`, 2>
  ) =>
    universalCreateDiscountRule(
      discountRule,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDiscountRuleSignature {
  /**
   * Creates a new discount rule.
   *
   *
   * The `createDiscountRule()` function returns a Promise that resolves to the new discount rule when it's created.
   * @param - Discount rule info.
   * @returns Discount rule.
   */
  (
    discountRule: NonNullablePaths<DiscountRule, `discounts` | `name`, 2>
  ): Promise<
    NonNullablePaths<
      DiscountRule,
      | `trigger.and.triggers`
      | `trigger.or.triggers`
      | `trigger.customTrigger._id`
      | `trigger.customTrigger.appId`
      | `trigger.subtotalRange.scopes`
      | `trigger.subtotalRange.scopes.${number}.catalogItemFilter.catalogAppId`
      | `trigger.subtotalRange.scopes.${number}.customFilter.appId`
      | `trigger.subtotalRange.scopes.${number}._id`
      | `trigger.subtotalRange.scopes.${number}.type`
      | `trigger.itemQuantityRange.scopes`
      | `trigger.customerEligibility.individualMembersInfo.memberIds`
      | `trigger.customerEligibility.eligibilityType`
      | `trigger.triggerType`
      | `discounts.values`
      | `discounts.values.${number}.percentage`
      | `discounts.values.${number}.fixedAmount`
      | `discounts.values.${number}.fixedPrice`
      | `discounts.values.${number}.targetType`
      | `discounts.values.${number}.discountType`
      | `status`
      | `usageCount`
      | `tags.privateTags.tagIds`,
      7
    > & {
      __applicationErrorsType?: CreateDiscountRuleApplicationErrors;
      __validationErrorsType?: CreateDiscountRuleValidationErrors;
    }
  >;
}

export function getDiscountRule(
  httpClient: HttpClient
): GetDiscountRuleSignature {
  return (discountRuleId: string) =>
    universalGetDiscountRule(
      discountRuleId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDiscountRuleSignature {
  /**
   * Retrieves a discount rule.
   *
   *
   * The `getDiscountRule()` function returns a Promise that resolves when the specified discount rule is retrieved.
   * @param - ID of the discount rule to retrieve.
   * @returns The requested discount rule.
   */
  (discountRuleId: string): Promise<
    NonNullablePaths<
      DiscountRule,
      | `trigger.and.triggers`
      | `trigger.or.triggers`
      | `trigger.customTrigger._id`
      | `trigger.customTrigger.appId`
      | `trigger.subtotalRange.scopes`
      | `trigger.subtotalRange.scopes.${number}.catalogItemFilter.catalogAppId`
      | `trigger.subtotalRange.scopes.${number}.customFilter.appId`
      | `trigger.subtotalRange.scopes.${number}._id`
      | `trigger.subtotalRange.scopes.${number}.type`
      | `trigger.itemQuantityRange.scopes`
      | `trigger.customerEligibility.individualMembersInfo.memberIds`
      | `trigger.customerEligibility.eligibilityType`
      | `trigger.triggerType`
      | `discounts.values`
      | `discounts.values.${number}.percentage`
      | `discounts.values.${number}.fixedAmount`
      | `discounts.values.${number}.fixedPrice`
      | `discounts.values.${number}.targetType`
      | `discounts.values.${number}.discountType`
      | `status`
      | `usageCount`
      | `tags.privateTags.tagIds`,
      7
    >
  >;
}

export function updateDiscountRule(
  httpClient: HttpClient
): UpdateDiscountRuleSignature {
  return (
    _id: string,
    discountRule: NonNullablePaths<UpdateDiscountRule, `revision`, 2>
  ) =>
    universalUpdateDiscountRule(
      _id,
      discountRule,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateDiscountRuleSignature {
  /**
   * Updates a discount rule's properties.
   *
   *
   * The `updateDiscountRule()` function returns a Promise that resolves when the specified discount rule's properties are updated.
   *
   * Each time the discount rule is updated, `revision` increments by 1. The existing `revision` must be included when updating the discount rule. This ensures you're working with the latest discount rule information, and it prevents unintended overwrites.
   * @param - Discount rule ID.
   * @param - Discount rule info.
   * @param - Discount rule info.
   * @returns Updated discount rule.
   */
  (
    _id: string,
    discountRule: NonNullablePaths<UpdateDiscountRule, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      DiscountRule,
      | `trigger.and.triggers`
      | `trigger.or.triggers`
      | `trigger.customTrigger._id`
      | `trigger.customTrigger.appId`
      | `trigger.subtotalRange.scopes`
      | `trigger.subtotalRange.scopes.${number}.catalogItemFilter.catalogAppId`
      | `trigger.subtotalRange.scopes.${number}.customFilter.appId`
      | `trigger.subtotalRange.scopes.${number}._id`
      | `trigger.subtotalRange.scopes.${number}.type`
      | `trigger.itemQuantityRange.scopes`
      | `trigger.customerEligibility.individualMembersInfo.memberIds`
      | `trigger.customerEligibility.eligibilityType`
      | `trigger.triggerType`
      | `discounts.values`
      | `discounts.values.${number}.percentage`
      | `discounts.values.${number}.fixedAmount`
      | `discounts.values.${number}.fixedPrice`
      | `discounts.values.${number}.targetType`
      | `discounts.values.${number}.discountType`
      | `status`
      | `usageCount`
      | `tags.privateTags.tagIds`,
      7
    >
  >;
}

export function deleteDiscountRule(
  httpClient: HttpClient
): DeleteDiscountRuleSignature {
  return (discountRuleId: string) =>
    universalDeleteDiscountRule(
      discountRuleId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteDiscountRuleSignature {
  /**
   * Deletes a discount rule.
   *
   *
   * The `deleteDiscountRule()` function returns a Promise that resolves when the specified discount rule is deleted.
   * @param - ID of the discount rule to delete.
   */
  (discountRuleId: string): Promise<void>;
}

export function queryDiscountRules(
  httpClient: HttpClient
): QueryDiscountRulesSignature {
  return () =>
    universalQueryDiscountRules(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryDiscountRulesSignature {
  /**
   * Creates a query to retrieve a list of discount rules.
   *
   *
   * The `queryDiscountRules()` function builds a query to retrieve a list of up to 100 discount rules, and returns a [`DiscountRulesQueryBuilder`](#discountrulesquerybuilder) object.
   *
   * The returned object contains the query definition which is typically used to run the query using the [`find()`](/discount-rules/discount-rules-query-builder/find) function.
   *
   * You can refine the query by chaining `DiscountRulesQueryBuilder` functions onto the query. `DiscountRulesQueryBuilder` functions enable you to sort, filter, and control the results queryDiscountRules() returns.
   *
   * By default, `queryDiscountRules()` sorts results by [`ascending("_id")`](/discount-rules/discount-rules-query-builder/ascending) by default. This can be overridden.
   *
   * To learn how to query posts, refer to the table below.
   *
   * The following `DiscountRulesQueryBuilder` functions are supported for the `queryDiscountRules()` function. For a full description of the discount rule object, see the object returned for the [`items`](/discount-rules/discount-rules-query-result/items) property in the `DiscountRulesQueryResult`.
   */
  (): DiscountRulesQueryBuilder;
}

export function typedQueryDiscountRules(
  httpClient: HttpClient
): TypedQueryDiscountRulesSignature {
  return (query: DiscountRuleQuery) =>
    universalTypedQueryDiscountRules(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryDiscountRulesSignature {
  /** */
  (query: DiscountRuleQuery): Promise<
    NonNullablePaths<
      QueryDiscountRulesResponse,
      | `discountRules`
      | `discountRules.${number}.trigger.customTrigger._id`
      | `discountRules.${number}.trigger.customTrigger.appId`
      | `discountRules.${number}.trigger.customerEligibility.eligibilityType`
      | `discountRules.${number}.trigger.triggerType`
      | `discountRules.${number}.status`
      | `discountRules.${number}.usageCount`,
      6
    >
  >;
}

export function bulkUpdateDiscountRuleTags(
  httpClient: HttpClient
): BulkUpdateDiscountRuleTagsSignature {
  return (
    discountRuleIds: string[],
    options?: BulkUpdateDiscountRuleTagsOptions
  ) =>
    universalBulkUpdateDiscountRuleTags(
      discountRuleIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateDiscountRuleTagsSignature {
  /**
   * Bulk update tags for discount rules by IDs.
   * @param - IDs of discount rules to update.
   */
  (
    discountRuleIds: string[],
    options?: BulkUpdateDiscountRuleTagsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateDiscountRuleTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateDiscountRuleTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateDiscountRuleTagsByFilter(
  httpClient: HttpClient
): BulkUpdateDiscountRuleTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateDiscountRuleTagsByFilterOptions
  ) =>
    universalBulkUpdateDiscountRuleTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateDiscountRuleTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple discount rules by filter.
   * An empty filter will update all discount rules.
   * A tag that appears both in the list of assign and unassign tags, will be assigned.
   * @param - Filter.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateDiscountRuleTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateDiscountRuleTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateDiscountRuleTagsByFilterApplicationErrors;
    }
  >;
}

export const onDiscountRuleCreated = EventDefinition(
  'wix.ecom.discounts.v1.discount_rule_created',
  true,
  (event: DiscountRuleCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.activeTimeInfo.start' },
            { path: 'entity.activeTimeInfo.end' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.discounts.values.percentage' }],
        },
      ])
    )
)<DiscountRuleCreatedEnvelope>();
export const onDiscountRuleDeleted = EventDefinition(
  'wix.ecom.discounts.v1.discount_rule_deleted',
  true,
  (event: DiscountRuleDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.activeTimeInfo.start' },
            { path: 'undefined.activeTimeInfo.end' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'undefined.discounts.values.percentage' }],
        },
      ])
    )
)<DiscountRuleDeletedEnvelope>();
export const onDiscountRuleUpdated = EventDefinition(
  'wix.ecom.discounts.v1.discount_rule_updated',
  true,
  (event: DiscountRuleUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.activeTimeInfo.start' },
            { path: 'entity.activeTimeInfo.end' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.activeTimeInfo.start' },
            { path: 'modifiedFields.activeTimeInfo.end' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.discounts.values.percentage' },
            { path: 'modifiedFields.discounts.values.percentage' },
          ],
        },
      ])
    )
)<DiscountRuleUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ActiveTimeInfo,
  Address,
  And,
  ApplicationError,
  AppliedDiscount,
  AppliedDiscountRule,
  AppliedSubjectType,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkUpdateDiscountRuleTagsByFilterOptions,
  BulkUpdateDiscountRuleTagsByFilterRequest,
  BulkUpdateDiscountRuleTagsByFilterResponse,
  BulkUpdateDiscountRuleTagsOptions,
  BulkUpdateDiscountRuleTagsRequest,
  BulkUpdateDiscountRuleTagsResponse,
  BulkUpdateDiscountRuleTagsResult,
  BuyXGetYInfo,
  BuyerInfo,
  CatalogItemFilter,
  CatalogReference,
  ContactSegments,
  ContactTags,
  CreateDiscountRuleRequest,
  CreateDiscountRuleResponse,
  CursorPaging,
  Cursors,
  Custom,
  CustomFilter,
  CustomerBuy,
  CustomerBuyConditionOneOf,
  CustomerEligibility,
  CustomerEligibilityOptionsOneOf,
  CustomerGet,
  DayOfWeek,
  DeleteDiscountRuleRequest,
  DeleteDiscountRuleResponse,
  Discount,
  DiscountConfig,
  DiscountDiscountOneOf,
  DiscountRule,
  DiscountRuleCreatedEnvelope,
  DiscountRuleDeletedEnvelope,
  DiscountRuleName,
  DiscountRuleQuerySpec,
  DiscountRuleUpdatedEnvelope,
  DiscountRuleUsageLimitReached,
  DiscountRulesQueryBuilder,
  DiscountRulesQueryResult,
  DiscountSettings,
  DiscountTrigger,
  DiscountTriggerTriggerOneOf,
  DiscountType,
  Discounts,
  DomainEvent,
  DomainEventBodyOneOf,
  EligibilityType,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetAppliedDiscountsRequest,
  GetAppliedDiscountsResponse,
  GetDiscountRuleRequest,
  GetDiscountRuleResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  IndividualMembers,
  ItemCombination,
  ItemCombinationLineItem,
  ItemMetadata,
  ItemQuantityRange,
  LineItem,
  MessageEnvelope,
  MultiCurrencyPrice,
  Or,
  PlatformPaging,
  PlatformPagingMetadata,
  PlatformQuery,
  PlatformQueryPagingMethodOneOf,
  QuantityBased,
  QueryDiscountRulesRequest,
  QueryDiscountRulesResponse,
  Recurrence,
  RestoreInfo,
  Scope,
  ScopeScopeItemsOneOf,
  ScopeType,
  ShippingInfo,
  SortOrder,
  Sorting,
  SpecificItemsInfo,
  Status,
  SubtotalRange,
  TagList,
  TagReference,
  Tags,
  TagsModified,
  TimeOfDay,
  TimeWindow,
  TriggerType,
  Type,
  UpdateDiscountRule,
  UpdateDiscountRuleRequest,
  UpdateDiscountRuleResponse,
  WebhookIdentityType,
  WeeklySchedule,
  utils,
} from './ecom-discounts-v1-discount-rule-discount-rules.universal.js';
